<template>
  <loading-element v-if="authStore.isLoading" />
  <div class="main bg-primary" v-else>
    <div class="rowSpaceBetween fullWidth mb-5">
      <TopDeck
        v-if="authStore.userInfos"
        firstElement="Catalogue"
        secondElement="de recettes"
        :coloredSecond="true"
        actionHeaderIcon="mdi-arrow-left"
        :actionheaderFunction="() => $router.push('/my_recipes')"
        actionHeaderColor="accent"
        actionHeaderTextColor="white"
      />
      <div class="rowCustom">
        <ThemeToggle />
        <AccountToggle />
      </div>
    </div>

    <div class="brc-container">
      <!-- Header -->
      <div class="brc-header">
        <div class="brc-header__left">
          <h1 class="brc-header__title">Recettes mises en avant</h1>
          <span class="brc-header__count">{{ filteredRecipes.length }} recette{{ filteredRecipes.length !== 1 ? 's' : '' }}</span>
        </div>
        <div class="brc-header__right">
          <!-- Recherche -->
          <div class="brc-search">
            <v-icon size="15" class="brc-search__icon">mdi-magnify</v-icon>
            <input
              v-model="searchQuery"
              class="brc-search__input"
              placeholder="Rechercher…"
            />
          </div>

          <!-- Boutons admin -->
          <template v-if="isAdmin">
            <button class="brc-btn-admin" @click="router.push('/base_recipes_import')">
              <v-icon size="14">mdi-upload</v-icon>
              Importer CSV
            </button>
            <button class="brc-btn-admin" @click="router.push('/base_recipes')">
              <v-icon size="14">mdi-plus</v-icon>
              Ajouter recette de base
            </button>
          </template>
        </div>
      </div>

      <!-- Pills catégories (multi) -->
      <div class="brc-filters">
        <button
          v-for="cat in categories"
          :key="cat"
          class="brc-pill"
          :class="{ 'brc-pill--active': cat === 'Toutes' ? selectedCategories.length === 0 : selectedCategories.includes(cat) }"
          @click="toggleCategory(cat)"
        >{{ cat === 'Toutes' ? 'Toutes' : $t(cat) }}</button>
      </div>

      <!-- Pills tags -->
      <div class="brc-filters brc-filters--tags">
        <button
          v-for="tag in ALL_TAGS"
          :key="tag"
          class="brc-pill brc-pill--tag"
          :class="{ 'brc-pill--active': selectedTags.includes(tag) }"
          @click="toggleTag(tag)"
        >{{ tag }}</button>
      </div>

      <!-- Pills kcal -->
      <div class="brc-filters brc-filters--kcal">
        <span class="brc-filter-label">Calories</span>
        <button
          v-for="range in KCAL_RANGES"
          :key="range.label"
          class="brc-pill brc-pill--kcal"
          :class="{ 'brc-pill--active': selectedKcal && selectedKcal.label === range.label }"
          @click="selectedKcal = selectedKcal && selectedKcal.label === range.label ? null : range"
        >{{ range.label }}</button>
      </div>

      <!-- Loader -->
      <div v-if="isLoadingRecipes" class="brc-loader">
        <v-progress-circular indeterminate color="accent" size="28" />
      </div>

      <!-- Vide -->
      <div v-else-if="!filteredRecipes.length" class="brc-empty">
        <v-icon size="36" style="opacity:0.25">mdi-food-off</v-icon>
        <p>Aucune recette à afficher.</p>
      </div>

      <!-- Grille -->
      <div v-else class="brc-grid">
        <BaseRecipeCard
          v-for="item in filteredRecipes"
          :key="item.id"
          :item="item"
          :disabled="addingIds.includes(item.id)"
          :isAdmin="isAdmin"
          @add="addToMyRecipes"
          @view="openDetails"
          @delete="confirmDelete"
        />
      </div>
    </div>

    <!-- Dialog suppression -->
    <v-dialog v-model="deleteDialog.open" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Supprimer la recette ?</v-card-title>
        <v-card-text>« {{ deleteDialog.item?.title }} » sera définitivement supprimée.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog.open = false">Annuler</v-btn>
          <v-btn color="error" :loading="deleteDialog.loading" @click="deleteRecipe">Supprimer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :timeout="2500">{{ snack.text }}</v-snackbar>
    <v-snackbar v-if="errorMessage" color="error" :timeout="3000">{{ errorMessage }}</v-snackbar>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import LoadingElement from "@/components/Animations/LoadingElement.vue";
import TopDeck from "@/components/Utils/TopDeck.vue";
import { useAuthStore } from "@/store/auth_store";
import ThemeToggle from "@/components/Utils/ThemeToggle.vue";
import AccountToggle from "@/components/Utils/AccountToggle.vue";
import { addBaseRecipeToUser, getBaseRecipes, deleteBaseRecipe } from "@/service/base_recipe_service";
import BaseRecipeCard from "@/components/Kitchen/Recipes/BaseRecipeCard.vue";

export default {
  name: "BaseRecipesCatalog",
  components: { LoadingElement, TopDeck, ThemeToggle, AccountToggle, BaseRecipeCard },
  setup() {
    const authStore = useAuthStore();
    const errorMessage = ref("");
    const snack = ref({ show: false, text: "" });
    const router = useRouter();
    const recipes = ref([]);
    const isLoadingRecipes = ref(false);
    const searchQuery = ref("");
    const selectedCategories = ref([]);
    const selectedKcal = ref(null);
    const KCAL_RANGES = [
      { label: '< 300', min: 0, max: 299 },
      { label: '300–400', min: 300, max: 400 },
      { label: '400–500', min: 400, max: 500 },
      { label: '500–600', min: 500, max: 600 },
      { label: '600–700', min: 600, max: 700 },
      { label: '> 700', min: 700, max: Infinity },
    ];
    function toggleCategory(cat) {
      if (cat === "Toutes") { selectedCategories.value = []; return; }
      const idx = selectedCategories.value.indexOf(cat);
      if (idx === -1) selectedCategories.value.push(cat);
      else selectedCategories.value.splice(idx, 1);
    }
    const addingIds = ref([]);
    const selectedTags = ref([]);
    const ALL_TAGS = ['Jus', 'Soupes', 'Cakes', 'Smoothies', 'Desserts', 'Chaud', 'Froid', 'Méditerranéen', 'Salades', 'Sauces', 'Pains'];

    function toggleTag(tag) {
      const idx = selectedTags.value.indexOf(tag);
      if (idx === -1) selectedTags.value.push(tag);
      else selectedTags.value.splice(idx, 1);
    }
    const isAdmin = computed(() => authStore.userInfos?.role === 'admin');
    const deleteDialog = ref({ open: false, item: null, loading: false });

    const CATEGORY_ORDER = ["Breakfast", "Starter", "Meal", "Dessert", "Snacks"];

    const categories = computed(() => {
      const cats = new Set();
      recipes.value.forEach((r) => {
        if (Array.isArray(r.categories)) r.categories.forEach(c => cats.add(c));
        else if (r.category) cats.add(r.category);
      });
      const sorted = CATEGORY_ORDER.filter((c) => cats.has(c));
      [...cats].forEach((c) => { if (!CATEGORY_ORDER.includes(c)) sorted.push(c); });
      return ["Toutes", ...sorted];
    });

    const filteredRecipes = computed(() => {
      const q = searchQuery.value.trim().toLowerCase();
      const tags = selectedTags.value;
      return recipes.value.filter((item) => {
        const title = (item.title || "").toLowerCase();
        const category = String(item.category || "").toLowerCase();
        const notes = (item.notes || "").toLowerCase();
        const matchSearch = !q || title.includes(q) || category.includes(q) || notes.includes(q);
        const itemCats = Array.isArray(item.categories) ? item.categories : (item.category ? [item.category] : []);
        const matchCategory = selectedCategories.value.length === 0 || selectedCategories.value.some((c) => itemCats.includes(c));
        const itemTags = Array.isArray(item.tags) ? item.tags : [];
        const matchTags = tags.length === 0 || tags.every((t) => itemTags.includes(t));
        const kcal = item.totals?.kcal || 0;
        const range = selectedKcal.value;
        const matchKcal = !range || (kcal >= range.min && kcal <= range.max);
        return matchSearch && matchCategory && matchTags && matchKcal;
      });
    });

    function openDetails(id) {
      router.push({ name: 'recipe_detail', params: { id }, query: { source: 'base' } });
    }

    async function addToMyRecipes(item) {
      if (!authStore?.userid) {
        errorMessage.value = "Utilisateur non connecté.";
        return;
      }
      const key = item.id || item.baseId || item.title;
      if (addingIds.value.includes(key)) return;
      addingIds.value.push(key);
      try {
        const res = await addBaseRecipeToUser(authStore.userid, item, item.id);
        if (res && res.existed) {
          snack.value = { show: true, text: "Vous possédez déjà cette recette." };
        } else {
          snack.value = { show: true, text: "Recette ajoutée à vos recettes ✅" };
        }
      } catch (e) {
        console.error("Erreur ajout recette utilisateur:", e);
        errorMessage.value = "Erreur lors de l'ajout de la recette.";
      } finally {
        const idx = addingIds.value.indexOf(key);
        if (idx !== -1) addingIds.value.splice(idx, 1);
      }
    }

    function confirmDelete(item) {
      deleteDialog.value = { open: true, item, loading: false };
    }

    async function deleteRecipe() {
      if (!deleteDialog.value.item) return;
      deleteDialog.value.loading = true;
      try {
        await deleteBaseRecipe(deleteDialog.value.item.id);
        recipes.value = recipes.value.filter(r => r.id !== deleteDialog.value.item.id);
        snack.value = { show: true, text: "Recette supprimée ✅" };
        deleteDialog.value.open = false;
      } catch (err) {
        console.error("Erreur suppression:", err);
        snack.value = { show: true, text: "Erreur lors de la suppression" };
      } finally {
        deleteDialog.value.loading = false;
      }
    }

    onMounted(() => {
      watch(
        () => authStore.userInfos,
        async () => {
          if (!authStore?.userid) return;
          try {
            isLoadingRecipes.value = true;
            const data = await getBaseRecipes();
            recipes.value = Object.entries(data).map(([id, value]) => ({ id, ...value }));
          } catch (error) {
            console.error("Erreur récupération recettes:", error);
            errorMessage.value = "Erreur lors du chargement des recettes.";
          } finally {
            isLoadingRecipes.value = false;
          }
        },
        { immediate: true }
      );
    });

    return {
      authStore,
      errorMessage,
      snack,
      router,
      recipes,
      searchQuery,
      selectedCategories,
      toggleCategory,
      selectedKcal,
      KCAL_RANGES,
      categories,
      addingIds,
      filteredRecipes,
      selectedTags,
      ALL_TAGS,
      toggleTag,
      isLoadingRecipes,
      isAdmin,
      openDetails,
      addToMyRecipes,
      deleteDialog,
      confirmDelete,
      deleteRecipe,
    };
  },
};
</script>

<style scoped>
/* Container */
.brc-container {
  background: rgb(var(--v-theme-secondary));
  border-radius: 16px;
  padding: 20px 20px 28px;
}

/* Header */
.brc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 18px;
}
.brc-header__left {
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.brc-header__title {
  font-size: 17px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
  margin: 0;
}
.brc-header__count {
  font-size: 12px;
  color: rgba(var(--v-theme-text), 0.4);
}
.brc-header__right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* Recherche */
.brc-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgb(var(--v-theme-tertiary));
  border: 0.5px solid rgba(var(--v-theme-text), 0.1);
  border-radius: 999px;
  padding: 7px 14px;
}
.brc-search__icon {
  color: rgba(var(--v-theme-text), 0.35);
  flex-shrink: 0;
}
.brc-search__input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 13px;
  color: rgb(var(--v-theme-text));
  font-family: inherit;
  width: 150px;
}
.brc-search__input::placeholder {
  color: rgba(var(--v-theme-text), 0.35);
}

/* Boutons admin */
.brc-btn-admin {
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(var(--v-theme-accent), 0.08);
  color: rgb(var(--v-theme-accent));
  border: 0.5px solid rgba(var(--v-theme-accent), 0.25);
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  transition: background 0.15s;
}
.brc-btn-admin:hover {
  background: rgba(var(--v-theme-accent), 0.15);
}

/* Pills filtres */
.brc-filters {
  display: flex;
  gap: 7px;
  margin-bottom: 20px;
  overflow-x: auto;
  padding-bottom: 2px;
  scrollbar-width: none;
}
.brc-filters::-webkit-scrollbar { display: none; }

.brc-pill {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 400;
  cursor: pointer;
  white-space: nowrap;
  border: 0.5px solid rgba(var(--v-theme-text), 0.18);
  color: rgba(var(--v-theme-text), 0.6);
  background: transparent;
  font-family: inherit;
  transition: all 0.15s;
}
.brc-pill:hover:not(.brc-pill--active) {
  background: rgba(var(--v-theme-text), 0.05);
}
.brc-pill--active {
  background: #1A1615;
  color: #fff;
  border-color: #1A1615;
}

/* Séparateur visuel entre rangées de pills */
.brc-filters--tags,
.brc-filters--kcal {
  border-top: 0.5px solid rgba(var(--v-theme-text), 0.08);
  padding-top: 12px;
  margin-top: -4px;
}
.brc-pill--tag,
.brc-pill--kcal {
  font-size: 11px;
}
.brc-filter-label {
  font-size: 11px;
  color: rgba(var(--v-theme-text), 0.35);
  white-space: nowrap;
  align-self: center;
  flex-shrink: 0;
}

/* Loader / vide */
.brc-loader, .brc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 0;
  color: rgba(var(--v-theme-text), 0.35);
  font-size: 13px;
}

/* Grille */
.brc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 20px;
}

/* Utils */
.rowCustom {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
}
</style>
